import AppButton from '@nxsys/components/AppButton';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import TaxWeekMonthSelect from '@nxsys/components/AppTaxWeekMonthSelect';
import {
  useGetSafeRecErrorList,
  useResendSafeRec,
} from '@nxsys/hooks/api/dashboard';
import { SafeRecErrorRow } from '@nxsys/types/api/dashboard';
import { getTaxWeekTaxYear } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { ColumnsType } from 'antd/lib/table';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';

type Props = {
  isOpen: boolean;
  toggle: () => void;
  cbAfterAction: () => void;
};

const SafeRecErrorModal: React.FC<Props> = ({
  isOpen,
  toggle,
  cbAfterAction,
}) => {
  const { taxYear: currentTaxYear } = getTaxWeekTaxYear();

  // Open scoped to the whole current tax year (no week filter) so the list
  // total matches the tile's "Submission Errors" count. The user can narrow by
  // tax week from the filter afterwards.
  const [taxYear, setTaxYear] = useState<number | undefined>(currentTaxYear);
  const [taxWeek, setTaxWeek] = useState<number | undefined>(undefined);
  const [selectedRows, setSelectedRows] = useState<SafeRecErrorRow[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState<string | undefined>(undefined);

  const { value, loading, run: fetchList } = useGetSafeRecErrorList();
  const { loading: resendLoading, run: resend } = useResendSafeRec();

  const list = value?.data ?? [];
  const totalDocs = value?.totalDocs ?? 0;

  const fetch = useCallback(
    (params: {
      taxYear?: number;
      taxWeek?: number;
      page?: number;
      size?: number;
      search?: string;
    }) => {
      fetchList({
        taxYear: params.taxYear,
        taxWeek: params.taxWeek,
        page: params.page ?? 0,
        size: params.size ?? 20,
        search: params.search,
        statusFilter: 'all',
      });
    },
    [fetchList],
  );

  useEffect(() => {
    if (isOpen) {
      fetch({ taxYear: currentTaxYear, taxWeek: undefined, page: 0, size: 20 });
    }
  }, [isOpen]);

  const handleChangePagination = (newPage: number, newSize: number) => {
    const resetPage = newSize !== pageSize;
    const nextPage = resetPage ? 0 : newPage - 1;
    setPage(nextPage);
    setPageSize(newSize);
    fetch({ taxYear, taxWeek, page: nextPage, size: newSize, search });
  };

  const handleSearch = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
      setPage(0);
      fetch({ taxYear, taxWeek, page: 0, size: pageSize, search: e.target.value });
    }, 500),
    [taxYear, taxWeek, pageSize],
  );

  const handleTaxYearFilter = (filters: { taxYear?: number }) => {
    setTaxYear(filters.taxYear);
    setPage(0);
    fetch({ taxYear: filters.taxYear, taxWeek, page: 0, size: pageSize, search });
  };

  const handleTaxWeekFilter = (filters: { taxPeriod?: number }) => {
    setTaxWeek(filters.taxPeriod);
    setPage(0);
    fetch({ taxYear, taxWeek: filters.taxPeriod, page: 0, size: pageSize, search });
  };

  const handleResend = () => {
    if (selectedRows.length === 0) {
      showMessage({ type: 'warning', content: 'No rows selected.' });
      return;
    }
    resend({ ids: selectedRows.map((r) => r.payrollId) }).then((res) => {
      if (res?.success) {
        showMessage({ type: 'success', content: 'SafeRec resend queued successfully.' });
        setSelectedRows([]);
        fetch({ taxYear, taxWeek, page, size: pageSize, search });
        cbAfterAction();
      } else {
        showMessage({
          type: 'error',
          content:
            (res as any)?.error ||
            (res as any)?.errorMessage ||
            'SafeRec resend failed. Some payslips may have no self-bill to send.',
        });
      }
    });
  };

  const columns: ColumnsType<SafeRecErrorRow> = [
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (v: string) =>
        v === 'NOT_SENT' ? 'Not Sent' : v === 'FAILED' ? 'Failed' : '_',
    },
    {
      title: 'Error',
      dataIndex: 'safeRecErrorMsg',
      width: 300,
      render: (v: string) => v || '_',
    },
    {
      title: 'Tax Period',
      align: 'right',
      render: (record: SafeRecErrorRow) =>
        `${record.taxPeriod || ''}/${record.taxYear || ''}`,
    },
    {
      title: 'Employee ID',
      dataIndex: 'subcontractorId',
      render: (v: string) => v || '_',
    },
    {
      title: 'Employee Name',
      dataIndex: 'subcontractorName',
      render: (v: string) => v || '_',
    },
    {
      title: 'Company',
      dataIndex: 'companyName',
      render: (v: string) => v || '_',
    },
  ];

  return (
    <AppModalContainer
      title={
        <div className="flex items-center justify-between w-full gap-x-[8px]">
          <div className="whitespace-nowrap">SafeRec Submission Errors</div>
          <AppButton
            name="Resend Selected"
            loading={resendLoading}
            disabled={resendLoading || selectedRows.length === 0}
            onClick={handleResend}
          />
        </div>
      }
      isOpen={isOpen}
      toggle={toggle}
      hiddenFooter
      className="max-sm:w-[100vw]"
      width={1200}
      minHeight="0"
      noPaddingBody
      titleFullWidth
    >
      <div className="flex justify-between flex-wrap w-full gap-4 my-[20px] px-[10px]">
        <div className="flex flex-wrap gap-4">
          <AppInputSearch onChange={handleSearch} />
        </div>
        <div className="flex flex-wrap gap-4 items-end">
          <TaxYearSelect
            initValue={currentTaxYear}
            onFilter={handleTaxYearFilter}
          />
          <TaxWeekMonthSelect
            initValue={taxWeek}
            onFilter={handleTaxWeekFilter}
            allowClear
          />
        </div>
      </div>
      <AppsContent>
        <AppTableContainer
          rowKey="payrollId"
          data={list}
          loading={loading}
          page={page}
          total={totalDocs}
          pageSize={pageSize}
          scroll={{ x: 1200 }}
          columns={columns}
          onChangePagination={handleChangePagination}
          rowSelection={{
            type: 'checkbox',
            onChange: (_keys: React.Key[], rows: SafeRecErrorRow[]) => {
              setSelectedRows(rows);
            },
          }}
        />
      </AppsContent>
    </AppModalContainer>
  );
};

export default SafeRecErrorModal;
